import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { EyebrowBlock } from '../../domain/types';
import {
  EYEBROW_FONT_SIZE,
  EYEBROW_FONT_WEIGHT,
  EYEBROW_TEXT_TRANSFORM,
} from '../../domain/blocks/eyebrow';
import {
  resolveEyebrowRuleColor,
  resolveEyebrowTextColor,
  type TextTheme,
} from '../../domain/text-theme';
import {
  emailBuilderDisplayedLabelFontWeight,
  emailBuilderRichTextStoredHtml,
  isEmailBuilderRichTextEmpty,
} from '../../editing/richText/emailBuilderRichTextHtml';
import { padStyle, presentationTableProps } from '../table';
import type { BlockRenderProps } from './types';

const RULE_GAP = 12;
const EYEBROW_LINE_HEIGHT = 1.2;

const cellReset: CSSProperties = {
  fontSize: 0,
  lineHeight: 0,
};

export function eyebrowTextStyle(
  block: EyebrowBlock,
  textTheme: TextTheme,
): CSSProperties {
  return {
    color: resolveEyebrowTextColor(block, textTheme),
    fontFamily: textTheme.headingFontFamily,
    fontSize: block.fontSize ?? EYEBROW_FONT_SIZE,
    fontWeight: emailBuilderDisplayedLabelFontWeight(block.text, block.fontWeight ?? EYEBROW_FONT_WEIGHT),
    letterSpacing: '0.08em',
    lineHeight: EYEBROW_LINE_HEIGHT,
    textTransform: block.textTransform ?? EYEBROW_TEXT_TRANSFORM,
    display: 'inline-block',
  };
}

function RuleCell({
  ruleColor,
  paddingLeft = 0,
  paddingRight = 0,
  width = '100%',
}: {
  ruleColor: string;
  paddingLeft?: number;
  paddingRight?: number;
  width?: string;
}) {
  return (
    <td
      style={{
        width,
        verticalAlign: 'middle',
        paddingLeft,
        paddingRight,
        ...cellReset,
      }}>
      <table
        {...presentationTableProps()}
        width="100%"
        style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td
              style={{
                height: 1,
                backgroundColor: ruleColor,
                fontSize: 0,
                lineHeight: 0,
              }}
            />
          </tr>
        </tbody>
      </table>
    </td>
  );
}

function renderEyebrowLabelContent(label: string): ReactNode {
  if (!/<[a-z][\s\S]*>/i.test(label)) {
    return label;
  }

  return (
    <span
      dangerouslySetInnerHTML={{
        __html: emailBuilderRichTextStoredHtml("label", label),
      }}
    />
  );
}

export function renderEyebrowBlock({
  block,
  textTheme,
  textSlot,
}: BlockRenderProps & { block: EyebrowBlock; textSlot?: ReactNode }) {
  const ruleColor = resolveEyebrowRuleColor(block, textTheme);
  const textStyle = eyebrowTextStyle(block, textTheme);
  const textCell = (
    <td
      key="text"
      style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', ...cellReset }}>
      {textSlot ?? (
        <span style={textStyle}>
          {isEmailBuilderRichTextEmpty(block.text)
            ? null
            : renderEyebrowLabelContent(block.text)}
        </span>
      )}
    </td>
  );

  const cells: ReactNode[] = [];
  const halfRule = block.rule === 'both';
  if (block.rule === 'before' || block.rule === 'both') {
    cells.push(
      <RuleCell
        key="rule-before"
        ruleColor={ruleColor}
        width={halfRule ? '50%' : '100%'}
        paddingRight={RULE_GAP}
      />,
    );
  }
  cells.push(textCell);
  if (block.rule === 'after' || block.rule === 'both') {
    cells.push(
      <RuleCell
        key="rule-after"
        ruleColor={ruleColor}
        width={halfRule ? '50%' : '100%'}
        paddingLeft={RULE_GAP}
      />,
    );
  }

  return (
    <table {...presentationTableProps()}>
      <tbody>
        <tr>
          <td style={padStyle(block.padding)}>
            <table
              {...presentationTableProps()}
              width="100%"
              style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>{cells}</tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
}
